import React, { Fragment, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import Button from '@ott/button';
import FirstErrorShower, { prepareRHFErrorsDeeply } from '@ott/first-error-shower';
import Input from '@ott/input';
import { l10n, l10nhtml } from '@ott/l10n';
import { limiter } from '@ott/superagent';
import { useEnterSubmit } from '@ott/utility-enter-submit';

import { yupResolver } from '@hookform/resolvers/yup';

import parseError from '../../helpers/parseError';
import { signInScheme } from '../../helpers/validationSchema';
import { getBasicUserInfo, enter, oAuthEnterSuccess } from '../../redux/auth/actions';
import { authSelector } from '../../redux/auth/selectors';
import { AuthDispatch } from '../../redux/auth/types';
import Social from '../Social';
import { FORM_FIELDS } from './helpers/constants';
import { FormData } from './types';

import styles from './SigninForm.scss';

const componentName = 'SigninForm';

type Props = {
  reqHost: string;
  withSocialAuth: boolean;
  onForgotPass: VoidFunction;
  requestSignup: VoidFunction;
};

const SigninForm = ({ reqHost, withSocialAuth, onForgotPass, requestSignup }: Props) => {
  const dispatch = useDispatch<AuthDispatch>();
  const [submitTime, setSubmitTime] = useState<number | null>(null);
  const { formRef, submitRef } = useEnterSubmit();
  const { enterStatus, infoStatus, oAuthUrl, oAuthModal, oAuthStatus } = useSelector(authSelector);

  const form = useForm<FormData>({
    shouldFocusError: true,
    resolver: yupResolver(signInScheme),
  });

  const { errors, isSubmitSuccessful, isSubmitted } = form.formState;

  useEffect(() => {
    if (oAuthUrl) {
      showOAuthWindow();
    }
  }, [oAuthUrl]);

  const showOAuthWindow = () => {
    if (!oAuthModal || !oAuthUrl) {
      return;
    }

    oAuthModal.location.href = oAuthUrl;

    const checkWindow = () => {
      if (oAuthModal.closed) {
        dispatch(getBasicUserInfo({ reqHost }));
        dispatch(oAuthEnterSuccess());
      } else {
        setTimeout(() => {
          checkWindow();
        }, 1000);
      }
    };

    checkWindow();
  };

  const submit = async ({ email, password }: FormData) => {
    setSubmitTime(Date.now());

    dispatch(enter({ email, password, reqHost }));
  };

  const renderSocialAuth = () => {
    if (!withSocialAuth) {
      return <div className={styles.text}>{l10n('auth.withEmail')}</div>;
    }

    return (
      <Fragment>
        <div className={styles.text}>{l10n('auth.withProfile')}</div>
        <Social />
      </Fragment>
    );
  };

  const renderError = () => {
    const { errorData } = enterStatus;

    if (!errorData) {
      return null;
    }

    return (
      <div data-locator={`${componentName}--error`} className={styles.error}>
        {parseError(errorData)}
      </div>
    );
  };

  const renderRegisterLink = () => {
    if (!requestSignup) {
      return null;
    }

    return (
      <div className={styles.signinFormFooter}>
        {l10n('auth.firstTime')}{' '}
        <span
          data-locator={`${componentName}--goToRegister`}
          className={styles.link}
          onClick={requestSignup}
        >
          {l10n('auth.signUp')}
        </span>
      </div>
    );
  };

  const preparedErrors = prepareRHFErrorsDeeply(errors);
  const isLoading = oAuthStatus.loading || enterStatus.loading || infoStatus.loading;

  return (
    <div className={styles.SigninForm} data-locator={componentName}>
      <form onSubmit={form.handleSubmit(submit)} ref={formRef}>
        <FirstErrorShower
          errors={preparedErrors}
          show={!isSubmitSuccessful && isSubmitted}
          useRHFErrors={true}
          container={formRef.current}
          overlayContainer={formRef.current}
          time={submitTime}
        />
        {renderSocialAuth()}
        {renderError()}
        <div className={styles.inputWrapper}>
          <Controller
            control={form.control}
            name={FORM_FIELDS.email}
            render={({ field, fieldState }) => (
              <Input
                {...field}
                label={l10n('auth.email')}
                isDisabled={isLoading}
                hasError={(isSubmitted || fieldState.isTouched) && fieldState.invalid}
                autoComplete="on"
                autoFocus={true}
                specificLocators={`${componentName}--email`}
              />
            )}
          />
        </div>
        <div className={styles.inputWrapper}>
          <Controller
            control={form.control}
            name={FORM_FIELDS.password}
            render={({ field, fieldState }) => (
              <Input
                {...field}
                label={l10n('auth.password')}
                type="password"
                isDisabled={isLoading}
                hasError={(isSubmitted || fieldState.isTouched) && fieldState.invalid}
                autoComplete="current-password"
                autoCorrect="off"
                autoCapitalize="off"
                specificLocators={`${componentName}--pwd`}
              />
            )}
          />
        </div>
        <div className={styles.isRight}>
          <span
            data-locator={`${componentName}--forgotPwd`}
            className={styles.link}
            onClick={onForgotPass}
          >
            {l10n('auth.forgotPassword')}
          </span>
        </div>
        <limiter.CaptchaContainer className={styles.captcha} name="auth-enter" />
        <div data-locator={`${componentName}--agreement`} className={styles.agreement}>
          {l10nhtml('auth.personalDataAgreementAuth')}
        </div>
        <Button
          innerRef={submitRef}
          className={styles.button}
          type="submit"
          isDisabled={enterStatus.loading}
          specificLocators={[`${componentName}--submit`]}
          withFullWidth={true}
          isLoading={isLoading}
        >
          {l10n('auth.signIn')}
        </Button>
      </form>
      {renderRegisterLink()}
    </div>
  );
};

export default SigninForm;
